/**
 * Skin Storage — per-tank visual customization persistence via localStorage.
 *
 * Each tank can have overrides for body shape, turret nub, color, and border style.
 * Overrides are keyed by tankId. Missing keys = use tank defaults.
 */

const STORAGE_KEY = 'geometry-blast-skins';

export interface TankSkinOverride {
  bodyShape?: string;   // tankId whose body shape to use
  nubType?: string;     // nub ID (tank-based or custom)
  color?: number;       // hex color override
  borderStyle?: string; // border style ID (e.g. 'rainbow', 'gold_shimmer')
  title?: string;       // title ID from TITLES array (empty/undefined = no title)
  titleColor?: number;  // hex color for title text (undefined = use tank color)
  titleRainbow?: boolean; // true = rainbow cycling hue override
}

export type SkinStore = Record<string, TankSkinOverride>;

// ─── Color Wheel (36 colors) ─────────────────────────────────────────────────

function hsl(h: number, s: number, l: number): number {
  // HSL to hex conversion
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  const r = Math.round(f(0) * 255);
  const g = Math.round(f(8) * 255);
  const b = Math.round(f(4) * 255);
  return (r << 16) | (g << 8) | b;
}

export const SKIN_COLORS: { label: string; color: number }[] = [
  // Row 1: Pure hues (saturated, bright)
  { label: 'Red',          color: 0xEE2233 },
  { label: 'Vermillion',   color: 0xEE4411 },
  { label: 'Orange',       color: 0xEE6600 },
  { label: 'Amber',        color: 0xEEAA00 },
  { label: 'Yellow',       color: 0xDDEE11 },
  { label: 'Chartreuse',   color: 0x88EE11 },
  { label: 'Green',        color: 0x22CC44 },
  { label: 'Emerald',      color: 0x22DD88 },
  { label: 'Cyan',         color: 0x22AAAA },
  // Row 2: Cool hues
  { label: 'Teal',         color: 0x22BBCC },
  { label: 'Sky',          color: 0x2299EE },
  { label: 'Azure',        color: 0x3366EE },
  { label: 'Blue',         color: 0x3344DD },
  { label: 'Indigo',       color: 0x4422CC },
  { label: 'Violet',       color: 0x6622AA },
  { label: 'Purple',       color: 0x8822CC },
  { label: 'Magenta',      color: 0xDD1177 },
  { label: 'Rose',         color: 0xEE2266 },
  // Row 3: Pastels + light tones
  { label: 'Coral',        color: 0xFF7766 },
  { label: 'Peach',        color: 0xFFAA88 },
  { label: 'Cream',        color: 0xFFDDAA },
  { label: 'Mint',         color: 0x88FFAA },
  { label: 'Seafoam',      color: 0x77DDCC },
  { label: 'Ice',          color: 0x99DDFF },
  { label: 'Lavender',     color: 0xBB99FF },
  { label: 'Pink',         color: 0xFF88CC },
  { label: 'Blush',        color: 0xFF99AA },
  // Row 4: Dark/muted tones + metallics
  { label: 'Crimson',      color: 0x881122 },
  { label: 'Rust',         color: 0x884422 },
  { label: 'Olive',        color: 0x6B8F5E },
  { label: 'Forest',       color: 0x226633 },
  { label: 'Midnight',     color: 0x223366 },
  { label: 'Gunmetal',     color: 0x556677 },
  { label: 'Silver',       color: 0xAABBCC },
  { label: 'Gold',         color: 0xDDAA33 },
  { label: 'White',        color: 0xDDDDDD },
];

// ─── Body Shapes (11 tank-based) ─────────────────────────────────────────────

export const BODY_SHAPE_IDS = [
  'mortar', 'bouncer', 'duelist', 'terraformer',
  'scatter', 'launcher', 'gyro', 'orbital', 'vortex',
  'shooter', 'cupid',
];

// ─── Nub Types (9 originals + 30 custom) ─────────────────────────────────────

export const NUB_TYPE_IDS = [
  // Original 9 (tank-based)
  'mortar', 'launcher', 'bouncer', 'terraformer',
  'scatter', 'orbital', 'vortex', 'gyro', 'duelist',
  // Custom 30
  'trident',       // 3-pronged fork
  'claw',          // curved claw hooks
  'crown',         // 3-point crown
  'anchor',        // ship anchor shape
  'crescent',      // moon crescent
  'lightning',     // zigzag bolt
  'flame',         // wavy flame tongues
  'snowflake',     // 6-arm crystal
  'crosshair_dot', // crosshair with center dot
  'atom',          // 3 elliptical orbits
  'eye',           // evil eye
  'hook',          // pirate hook curve
  'skull',         // skull outline
  'heart',         // heart shape
  'music_note',    // 8th note
  'biohazard',     // 3-arc biohazard
  'radiation',     // radiation trefoil
  'gear',          // 8-tooth gear
  'saw',           // circular saw teeth
  'drill',         // spiral drill point
  'mace',          // spiked ball
  'hammer',        // Thor hammer
  'scythe',        // curved blade
  'shuriken',      // 4-point ninja star
  'wings',         // spread wing pair
  'horns',         // devil horns
  'halo',          // angel ring
  'clover',        // 3-leaf clover
  'paw',           // paw print
  'pixel',         // 2x2 pixel block
];

// Nub display names for shop labels
export const NUB_LABELS: Record<string, string> = {
  mortar: 'Bore Ring', launcher: 'Arrow', bouncer: 'Diamond',
  terraformer: 'Hexagon', scatter: 'Asterisk', orbital: 'Reticle',
  vortex: 'Spiral', gyro: 'Propeller', duelist: 'Saber',
  trident: 'Trident', claw: 'Claw', crown: 'Crown',
  anchor: 'Anchor', crescent: 'Crescent', lightning: 'Lightning',
  flame: 'Flame', snowflake: 'Snowflake', crosshair_dot: 'Crosshair+',
  atom: 'Atom', eye: 'Eye', hook: 'Hook',
  skull: 'Skull', heart: 'Heart', music_note: 'Note',
  biohazard: 'Biohazard', radiation: 'Radiation', gear: 'Gear',
  saw: 'Saw', drill: 'Drill', mace: 'Mace',
  hammer: 'Hammer', scythe: 'Scythe', shuriken: 'Shuriken',
  wings: 'Wings', horns: 'Horns', halo: 'Halo',
  clover: 'Clover', paw: 'Paw', pixel: 'Pixel',
};

// ─── Border Styles ───────────────────────────────────────────────────────────

export const BORDER_STYLES: { id: string; label: string; color: number }[] = [
  { id: 'none',           label: 'None',         color: 0x333333 },
  { id: 'rainbow',        label: 'Rainbow',       color: 0xffffff },
  { id: 'gold_shimmer',   label: 'Gold Shimmer',  color: 0xDDAA33 },
  { id: 'silver_shimmer', label: 'Silver Shimmer', color: 0xAABBCC },
  { id: 'fire',           label: 'Fire',          color: 0xFF4400 },
  { id: 'ice',            label: 'Ice',           color: 0x66CCFF },
  { id: 'toxic',          label: 'Toxic',         color: 0x44FF22 },
  { id: 'void',           label: 'Void',          color: 0x6622AA },
  { id: 'blood',          label: 'Blood',         color: 0xCC1133 },
  { id: 'electric',       label: 'Electric',      color: 0x22EEFF },
  { id: 'sakura',         label: 'Sakura',        color: 0xFF88CC },
  { id: 'sunset',         label: 'Sunset',        color: 0xFF6622 },
];

// ─── Titles (30 + none) ──────────────────────────────────────────────────────

export const TITLES: { id: string; label: string }[] = [
  { id: 'none',             label: 'None' },
  { id: 'commander',        label: 'Commander' },
  { id: 'sniper',           label: 'Sniper' },
  { id: 'thor',             label: 'Thor' },
  { id: 'butt_pincher',     label: 'Butt Pincher' },
  { id: 'warlord',          label: 'Warlord' },
  { id: 'rookie',           label: 'Rookie' },
  { id: 'legend',           label: 'Legend' },
  { id: 'trash_panda',      label: 'Trash Panda' },
  { id: 'demolisher',       label: 'Demolisher' },
  { id: 'ghost',            label: 'Ghost' },
  { id: 'ace',              label: 'Ace' },
  { id: 'big_brain',        label: 'Big Brain' },
  { id: 'no_mercy',         label: 'No Mercy' },
  { id: 'potato',           label: 'Potato' },
  { id: 'send_help',        label: 'Send Help' },
  { id: 'certified_fresh',  label: 'Certified Fresh' },
  { id: 'menace',           label: 'Menace' },
  { id: 'untouchable',      label: 'Untouchable' },
  { id: 'overkill',         label: 'Overkill' },
  { id: 'bottom_feeder',    label: 'Bottom Feeder' },
  { id: 'mvp',              label: 'MVP' },
  { id: 'one_tap',          label: 'One Tap' },
  { id: 'chaos_agent',      label: 'Chaos Agent' },
  { id: 'try_hard',         label: 'Try Hard' },
  { id: 'rage_quit',        label: 'Rage Quit' },
  { id: 'gg_ez',            label: 'GG EZ' },
  { id: 'skill_issue',      label: 'Skill Issue' },
  { id: 'main_character',   label: 'Main Character' },
  { id: 'final_boss',       label: 'Final Boss' },
  { id: 'touch_grass',      label: 'Touch Grass' },
];

// ─── Title Colors (rainbow + 36 skin colors) ────────────────────────────────

export const TITLE_COLORS: { label: string; color: number; rainbow?: boolean }[] = [
  { label: 'Rainbow', color: 0xffffff, rainbow: true },
  ...SKIN_COLORS,
];

// ─── Persistence ─────────────────────────────────────────────────────────────

function loadSkins(): SkinStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as SkinStore;
  } catch {
    return {};
  }
}

function saveSkins(store: SkinStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // localStorage full or unavailable — silently fail
  }
}

export function getSkinForTank(tankId: string): TankSkinOverride {
  const store = loadSkins();
  return store[tankId] ?? {};
}

export function setSkinProperty(tankId: string, prop: keyof TankSkinOverride, value: string | number | boolean): void {
  const store = loadSkins();
  if (!store[tankId]) store[tankId] = {};
  (store[tankId] as Record<string, string | number | boolean>)[prop] = value;
  saveSkins(store);
}

export function resetSkin(tankId: string): void {
  const store = loadSkins();
  delete store[tankId];
  saveSkins(store);
}
